import LegalShell, { Section } from "@/components/LegalShell";

export default function PrivacyPage() {
  return (
    <LegalShell
      testid="privacy-page"
      eyebrow="legal · privacy policy"
      title="Privacy Policy"
      lastUpdated="2026-05-18"
    >
      <Section heading="1. Introduction">
        <p>
          This Privacy Policy explains how{" "}
          <strong className="text-zinc-200">Super Founder Hub</strong>{" "}
          (&ldquo;we&rdquo;) collects, uses, and protects the personal information
          you share while using The Founder Mode Quest.
        </p>
      </Section>

      <Section heading="2. What we collect">
        <ul className="list-disc pl-6 space-y-1 marker:text-amber-400">
          <li>Your <strong className="text-zinc-200">email address</strong> and chosen <strong className="text-zinc-200">display name</strong> (used for authentication and personalization)</li>
          <li>Your <strong className="text-zinc-200">startup stage</strong>, <strong className="text-zinc-200">founder persona</strong>, and a short <strong className="text-zinc-200">startup description</strong> you provide during onboarding</li>
          <li>Your <strong className="text-zinc-200">conversation messages</strong> with the AI mentor, your quest <strong className="text-zinc-200">scores</strong>, and completion timestamps</li>
          <li>Payment metadata returned by Razorpay or Stripe (order id, payment id, signature) — <strong className="text-zinc-200">never credit card or bank account numbers</strong></li>
        </ul>
      </Section>

      <Section heading="3. Why we collect it">
        <ul className="list-disc pl-6 space-y-1 marker:text-amber-400">
          <li>Authentication (so you can resume your quest from any device)</li>
          <li>AI personalization (the mentor adapts its questions based on your stage and persona)</li>
          <li>Scoring and the monthly leaderboard</li>
          <li>Payment processing and access provisioning</li>
        </ul>
      </Section>

      <Section heading="4. Cookies and storage">
        <p>
          We do <strong className="text-zinc-200">not</strong> use cookies, browser
          local storage, or session storage. Authentication state is held in React
          memory only and is cleared when you close the tab. To resume, you re-enter
          your email on the landing page.
        </p>
      </Section>

      <Section heading="5. Third-party processors">
        <p>The following services process your data on our behalf:</p>
        <ul className="list-disc pl-6 space-y-1 marker:text-amber-400">
          <li><strong className="text-zinc-200">OpenAI</strong> — GPT-4o powers the AI mentor; your conversation messages are sent to OpenAI&apos;s API for inference. OpenAI&apos;s API data is not used to train models per their default API policy.</li>
          <li><strong className="text-zinc-200">Razorpay</strong> — handles all India (INR) payments</li>
          <li><strong className="text-zinc-200">Stripe</strong> — handles all international (USD) payments</li>
          <li><strong className="text-zinc-200">MongoDB</strong> — stores your user record, progress, and payment metadata</li>
        </ul>
        <p>
          Each provider has its own privacy policy and data-handling commitments.
        </p>
      </Section>

      <Section heading="6. Data retention">
        <p>
          We retain your account, progress, and payment records until you request
          deletion. To request deletion, email{" "}
          <a href="mailto:praveen@thefoundermode.in" className="text-amber-300 hover:text-amber-200">
            praveen@thefoundermode.in
          </a>{" "}
          from the address associated with your account. We will permanently delete
          your record within 30 days, subject to legal retention requirements (e.g.,
          tax invoices retained for the statutory period).
        </p>
      </Section>

      <Section heading="7. Your rights (India DPDP Act)">
        <p>
          Under India&apos;s Digital Personal Data Protection Act, you have the
          following rights with respect to your personal data:
        </p>
        <ul className="list-disc pl-6 space-y-1 marker:text-amber-400">
          <li>Right to access — request a copy of your data</li>
          <li>Right to correction — fix inaccurate data</li>
          <li>Right to erasure — request deletion (see Section 6)</li>
          <li>Right to grievance redressal — contact us at the email below</li>
        </ul>
      </Section>

      <Section heading="8. Security">
        <p>
          We use HTTPS in transit, MongoDB-managed encryption at rest, and store
          secrets (API keys, webhook secrets) only in server-side environment
          variables — never in client code. We follow industry best practices but no
          system is 100% secure; please use a unique email and avoid sharing access.
        </p>
      </Section>

      <Section heading="9. Children">
        <p>
          The service is not directed to anyone under 18 years of age. We do not
          knowingly collect data from minors. If you become aware that a minor has
          provided us data, contact us and we will delete it.
        </p>
      </Section>

      <Section heading="10. Updates">
        <p>
          We may update this Policy from time to time. Material changes will be
          announced on the landing page and the &ldquo;Last updated&rdquo; date at the
          top of this page will change. Continued use after an update constitutes
          acceptance of the new Policy.
        </p>
      </Section>

      <Section heading="11. Contact">
        <p>For any privacy concerns or data requests:</p>
        <ul className="list-none pl-0 space-y-1 text-zinc-300">
          <li>Email: <a href="mailto:praveen@thefoundermode.in" className="text-amber-300 hover:text-amber-200">praveen@thefoundermode.in</a></li>
          <li>Phone: +91 94483 10439</li>
          <li>Address: Super Founder Hub, Kanapura Main Road, Bangalore, Karnataka, India</li>
        </ul>
      </Section>
    </LegalShell>
  );
}
